import { useEffect, useRef, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { navLinks, navDropdowns, scheduleConsultUrl, LOGO } from "@/lib/site-core";

/** Fire the Zaraz click event when a header consultation CTA is clicked. */
function trackScheduleClick() {
  try {
    (
      window as unknown as { zaraz?: { track?: (n: string) => void } }
    ).zaraz?.track?.("schedule_consultation_click");
  } catch {
    /* tracking must never block navigation */
  }
}

export function Header({
  logoSrc,
  scheduleHref = scheduleConsultUrl,
}: {
  logoSrc?: string;
  scheduleHref?: string;
} = {}) {
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState<string | null>(null);
  const [desktopSub, setDesktopSub] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!desktopSub) return;
    function onDocClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setDesktopSub(null);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setDesktopSub(null);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [desktopSub]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-4 lg:gap-4 lg:px-6 xl:gap-6 xl:px-8">
        <a href="https://restorehair.com" className="flex-shrink-0">
          <img
            src={logoSrc ?? LOGO}
            alt="RESTORE Hair"
            width={416}
            height={64}
            sizes="(min-width: 768px) 234px, 208px"
            decoding="async"
            fetchPriority="high"
            className="h-8 w-auto md:h-9"
          />
        </a>
        <nav ref={navRef} className="hidden items-center gap-3 lg:flex xl:gap-6">
          {navLinks.map((l) => {
            const subItems = navDropdowns[l.label];
            if (subItems) {
              const isOpen = desktopSub === l.label;
              return (
                <div
                  key={l.label}
                  className="relative"
                  onMouseEnter={() => setDesktopSub(l.label)}
                  onMouseLeave={() => setDesktopSub((s) => (s === l.label ? null : s))}
                >
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    onClick={() => setDesktopSub(isOpen ? null : l.label)}
                    className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground transition-all hover:underline hover:underline-offset-4 xl:text-sm"
                  >
                    {l.label}
                    <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div
                    role="menu"
                    aria-label={l.label}
                    className={`absolute left-0 top-full z-50 min-w-[240px] max-w-[320px] border border-border bg-background shadow-lg transition-all duration-150 ${
                      isOpen ? "visible translate-y-0 opacity-100" : "invisible translate-y-1 opacity-0"
                    }`}
                  >
                    <div className="h-2 -mt-2" aria-hidden />
                    <ul className="py-2">
                      {subItems.map((s) => (
                        <li key={s.href}>
                          <a
                            role="menuitem"
                            href={s.href}
                            onClick={() => setDesktopSub(null)}
                            className="block whitespace-normal px-4 py-2 text-xs font-medium leading-snug text-foreground transition-colors hover:bg-muted xl:text-sm"
                          >
                            {s.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            }
            return (
              <a
                key={l.label}
                href={l.href}
                className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground transition-all hover:underline hover:underline-offset-4 xl:text-sm"
              >
                {l.label}
              </a>
            );
          })}
        </nav>
        <a
          href={scheduleHref}
          onClick={trackScheduleClick}
          className="hidden whitespace-nowrap border-b-4 border-foreground bg-primary px-3 py-2 text-center text-xs font-semibold leading-tight tracking-wider text-primary-foreground transition hover:bg-primary/90 md:block xl:px-5 xl:py-3 xl:text-sm"
        >
          Schedule Your
          <br />
          Free Consultation
        </a>
        <button
          aria-label="Toggle menu"
          className="lg:hidden"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <nav className="flex flex-col gap-1 px-4 py-3">
            {navLinks.map((l) => {
              const subItems = navDropdowns[l.label];
              if (subItems) {
                const isOpen = mobileSub === l.label;
                return (
                  <div key={l.label} className="border-b border-border/40 last:border-0">
                    <button
                      type="button"
                      onClick={() => setMobileSub(isOpen ? null : l.label)}
                      className="flex w-full items-center justify-between py-2 text-sm font-medium text-foreground transition-all hover:underline hover:underline-offset-4"
                    >
                      {l.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <ul className="mb-2 ml-3 flex flex-col gap-1 border-l border-border pl-3">
                        {subItems.map((s) => (
                          <li key={s.href}>
                            <a
                              href={s.href}
                              className="block py-1.5 text-sm text-foreground transition-colors hover:bg-muted"
                            >
                              {s.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return (
                <a
                  key={l.label}
                  href={l.href}
                  className="py-2 text-sm font-medium text-foreground transition-all hover:underline hover:underline-offset-4"
                >
                  {l.label}
                </a>
              );
            })}
            <a
              href={scheduleHref}
              onClick={trackScheduleClick}
              className="mt-2 bg-primary px-5 py-3 text-center text-sm font-semibold text-primary-foreground"
            >
              Schedule Your Free Consultation
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}